import React from 'react';

import {
  ActionIcon,
  Group,
  NumberInput,
  Radio,
  Stack,
  TextInput,
  Title,
  Tooltip,
  useMantineTheme,
} from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { IconWand } from '@tabler/icons-react';

import { FormValues } from './DrawerDiscountForm';

interface IGeneralSettingsSectionProps {
  form: UseFormReturnType<FormValues, (values: FormValues) => unknown>;
  isReadOnly?: boolean;
  disabled?: boolean;
}

const generateCode = (): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  return Array.from({ length: 10 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
};

const GeneralSettingsSection = ({
  form,
  isReadOnly = false,
  disabled = false,
}: IGeneralSettingsSectionProps): JSX.Element => {
  const theme = useMantineTheme();
  const isFixedAmount = form.values.valueType === 'FIXED_AMOUNT';
  const isPercentage = form.values.valueType === 'PERCENTAGE';

  return (
    <Stack>
      <Title order={3}>1. General Settings</Title>
      <TextInput
        {...form.getInputProps('name')}
        label='Name'
        placeholder='e.g. Summer Flash Sale'
        required
        disabled={disabled}
      />
      <Group align='flex-end' spacing={'12px'} noWrap>
        <TextInput
          {...form.getInputProps('code')}
          label='Code'
          description='Enter code (letters, digits, - or _) or Click “Magic Wand” icon to randomize.'
          placeholder='e.g. DISCOUNT26'
          required
          disabled={disabled || isReadOnly}
          sx={{ width: '100%' }}
        />
        <Tooltip label='Auto-generate code'>
          <ActionIcon
            onClick={() => form.setFieldValue('code', generateCode())}
            color='kiloBlue'
            disabled={disabled || isReadOnly}
            style={{ border: !isReadOnly ? '1px solid #15B8FF' : 'none' }}
            radius={'4px'}
            mb={'4px'}
          >
            <IconWand size={theme.fontSizes.xl} />
          </ActionIcon>
        </Tooltip>
      </Group>
      <Radio.Group {...form.getInputProps('valueType')} label='Value Type' required>
        <Stack spacing='xs' mt='xs'>
          <Radio value='PERCENTAGE' label='Percentage' disabled={disabled || isReadOnly} />
          <Radio value='FIXED_AMOUNT' label='Fixed Amount' disabled={disabled || isReadOnly} />
        </Stack>
      </Radio.Group>
      {isFixedAmount && (
        <NumberInput
          {...form.getInputProps('amount')}
          label='Value'
          required
          disabled={disabled || isReadOnly}
          min={0.01}
          precision={2}
          parser={value => value?.replace(/\$\s?|(,*)/g, '')}
          formatter={value => (value ? `$ ${value}` : '')}
        />
      )}
      {isPercentage && (
        <NumberInput
          {...form.getInputProps('percentage')}
          label='Value'
          required
          disabled={disabled || isReadOnly}
          min={0.01}
          max={100}
          precision={2}
          rightSection='%'
        />
      )}
    </Stack>
  );
};

export default GeneralSettingsSection;
